import { useEffect, useState } from "react";
import { Undo2, Redo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MAP_PANEL_Z } from "@/lib/map-panel-z";
import { cn } from "@/lib/utils";

const GPML_DRAWING_UNDO_STATE_EVENT = "gpml-drawing-undo-state";
const GPML_DRAWING_UNDO_CMD_EVENT = "gpml-drawing-undo-cmd";

type DrawingUndoStateDetail = { canUndo: boolean; canRedo: boolean };

interface MapDrawingModeBannerProps {
  mode: "create" | "edit";
  geometryName?: string | null;
  /** Показать подсказку по удалению вершины (сессия Mapbox Draw) */
  showVertexShortcut?: boolean;
  /** Есть несохранённые изменения в DrawingPanel */
  dirty?: boolean;
}

type MultiSessionDetail =
  | {
      kind: "multipolygon";
      ringsDone: number;
      currentVertices: number;
    }
  | {
      kind: "multilinestring";
      linesDone: number;
      currentVertices: number;
    }
  | {
      kind: "multipoint";
      pointsCount: number;
    };

function formatMultiSession(d: MultiSessionDetail): string {
  if (d.kind === "multipolygon") {
    return `MultiPolygon: готовых контуров — ${d.ringsDone}, в текущем — ${d.currentVertices} верш. Enter — завершить, Backspace — шаг назад.`;
  }
  if (d.kind === "multilinestring") {
    return `MultiLineString: линий — ${d.linesDone}, в текущей — ${d.currentVertices} верш. Двойной клик — зафиксировать линию. Enter — готово.`;
  }
  return `MultiPoint: точек — ${d.pointsCount}. Enter — завершить набор, Backspace — убрать последнюю.`;
}

export default function MapDrawingModeBanner({
  mode,
  geometryName,
  showVertexShortcut,
  dirty = false,
}: MapDrawingModeBannerProps) {
  const [multiSession, setMultiSession] = useState<MultiSessionDetail | null>(null);
  const [multiHint, setMultiHint] = useState<string | null>(null);
  const [invalidHint, setInvalidHint] = useState<string | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    if (mode !== "create") {
      setMultiSession(null);
      setMultiHint(null);
      setInvalidHint(null);
      return;
    }
    const onSession = (e: Event) => {
      const ce = e as CustomEvent<MultiSessionDetail>;
      if (ce.detail?.kind) {
        setMultiSession(ce.detail);
        setMultiHint(null);
      }
    };
    const onHint = (e: Event) => {
      const msg = (e as CustomEvent<{ message?: string }>).detail?.message;
      if (typeof msg === "string" && msg.trim()) setMultiHint(msg.trim());
    };
    const onInvalid = (e: Event) => {
      const msg = (e as CustomEvent<{ message?: string }>).detail?.message;
      if (typeof msg === "string" && msg.trim()) setInvalidHint(msg.trim());
    };
    window.addEventListener("gpml-multi-draw-session", onSession as EventListener);
    window.addEventListener("gpml-multi-draw-hint", onHint as EventListener);
    window.addEventListener("gpml-draw-invalid", onInvalid as EventListener);
    return () => {
      window.removeEventListener("gpml-multi-draw-session", onSession as EventListener);
      window.removeEventListener("gpml-multi-draw-hint", onHint as EventListener);
      window.removeEventListener("gpml-draw-invalid", onInvalid as EventListener);
    };
  }, [mode]);

  useEffect(() => {
    const onUndoState = (e: Event) => {
      const d = (e as CustomEvent<DrawingUndoStateDetail>).detail;
      if (!d) return;
      setCanUndo(Boolean(d.canUndo));
      setCanRedo(Boolean(d.canRedo));
    };
    window.addEventListener(GPML_DRAWING_UNDO_STATE_EVENT, onUndoState as EventListener);
    return () => {
      window.removeEventListener(GPML_DRAWING_UNDO_STATE_EVENT, onUndoState as EventListener);
      setCanUndo(false);
      setCanRedo(false);
    };
  }, [mode]);

  const dispatchUndoCmd = (action: "undo" | "redo") => {
    window.dispatchEvent(
      new CustomEvent(GPML_DRAWING_UNDO_CMD_EVENT, { detail: { action } }),
    );
  };

  const title =
    mode === "edit"
      ? geometryName
        ? `Редактирование: «${geometryName}»`
        : "Редактирование контура"
      : "Режим создания геометрии";

  const multiBlock =
    mode === "create" && multiSession ? (
      <div className="mt-1.5 rounded border border-primary/25 bg-primary/5 px-2 py-1.5 text-left text-[11px] leading-snug text-foreground md:text-xs">
        <span className="font-medium text-primary">Составная геометрия: </span>
        {formatMultiSession(multiSession)}
      </div>
    ) : null;

  const hintBlock =
    mode === "create" && multiHint ? (
      <div className="mt-1 text-[11px] text-amber-800 dark:text-amber-200" role="alert">
        {multiHint}
      </div>
    ) : null;

  const invalidBlock =
    mode === "create" && invalidHint ? (
      <div className="mt-1 text-[11px] font-medium text-red-700 dark:text-red-300" role="alert">
        {invalidHint}
      </div>
    ) : null;

  return (
    <div
      className={cn(
        "pointer-events-none absolute left-1/2 top-3 max-w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 rounded-md border border-border/80 bg-background/95 px-3 py-2 text-center text-xs shadow-md backdrop-blur-sm md:text-sm",
        MAP_PANEL_Z.drawingBanner,
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-center gap-2">
        <div className="font-medium text-foreground">{title}</div>
        <div className="pointer-events-auto flex items-center gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={!canUndo}
            title="Отменить (Ctrl/⌘+Z)"
            aria-label="Отменить шаг рисования"
            onClick={() => dispatchUndoCmd("undo")}
          >
            <Undo2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={!canRedo}
            title="Повторить (Ctrl/⌘+Shift+Z или Ctrl/⌘+Y)"
            aria-label="Повторить шаг рисования"
            onClick={() => dispatchUndoCmd("redo")}
          >
            <Redo2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {dirty && (
        <div className="mt-1 text-[11px] font-medium text-amber-800 dark:text-amber-200">
          Есть несохранённые изменения — сохраните или отмените перед уходом.
        </div>
      )}
      <div className="mt-1 text-muted-foreground">
        {mode === "edit" ? (
          <>
            Перетащите узлы на карте, затем нажмите «Сохранить» в панели внизу.
            {showVertexShortcut && (
              <span className="hidden sm:inline"> Del — удалить выбранную вершину.</span>
            )}
            <span className="hidden sm:inline"> Ctrl/⌘+Z — отменить шаг, Ctrl/⌘+Y — повторить.</span>
          </>
        ) : (
          <>
            Подсказки по шагам — в панели слева внизу. Пробел + перетаскивание — панорама без выхода из режима.
            <span className="hidden sm:inline"> Ctrl/⌘+Z — отменить шаг, Ctrl/⌘+Y — повторить.</span>
            {multiSession && (
              <span className="hidden sm:inline">
                {" "}
                Для MultiPolygon/MultiLine/MultiPoint используйте Enter и Backspace на карте (не в полях ввода).
              </span>
            )}
          </>
        )}
      </div>
      {multiBlock}
      {hintBlock}
      {invalidBlock}
      {mode === "create" && (
        <div className="mt-1 text-[11px] text-muted-foreground/90">
          Магнит: клик рядом с вершиной текущего чертежа притягивается (замыкание — у первой точки полигона).
        </div>
      )}
      {mode === "edit" && (
        <div className="mt-1 text-[11px] text-muted-foreground/90">
          Остальные контуры объекта показаны бледным цветом. Вершины с магнитом; самопересечение запрещено.
        </div>
      )}
    </div>
  );
}
